import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

interface ManagerPasswordPromptDialogProps {
  open: boolean
  title: string
  description: React.ReactNode
  confirmLabel: string
  onClose: () => void
  onConfirm: (password: string) => Promise<void>
}

// Shared password re-prompt for manager actions (Log in as user, Stop impersonating).
// Keeps the password in local state only and clears it on close so it never leaks
// into React DevTools / sessionStorage.
const ManagerPasswordPromptDialog: React.FC<ManagerPasswordPromptDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
}) => {
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (!open) {
      setPassword('')
      setError('')
      setShowPassword(false)
    }
  }, [open])

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleConfirm = async () => {
    if (!password) {
      setError('Password is required')
      return
    }
    try {
      setLoading(true)
      setError('')
      await onConfirm(password)
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            autoFocus
            type={showPassword ? 'text' : 'password'}
            label="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password && !loading) {
                e.preventDefault()
                void handleConfirm()
              }
            }}
            disabled={loading}
            size="small"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={() => setShowPassword(v => !v)}
                  sx={{ color: 'grey.500' }}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {/* data-voice="confirm": Douglas asks before pressing (#1363) — re-authenticates against the licensing server to start or end an impersonation session */}
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="info"
          disabled={loading || !password}
          data-voice="confirm"
        >
          {loading ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ManagerPasswordPromptDialog
